import { Column, ColumnEditorOptions } from 'primereact/column';
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';
import React from 'react';
import {
  useCreateEpdProductDangerMaterial,
  useDeleteProductDangerMaterials,
  useUpdateEpdProductDangerMaterial,
} from 'services/api/mutations';
import { useDangerMaterialsLookup, useEpdProductDangerMaterials } from 'services/api/queries';
import { EPDContentDeclarationProductType } from 'types/content-declaration/EPDContentDeclarationProductType';
import { ProductDangerMaterialModel } from 'types/content-declaration/ProductDangerMaterialModel';
import useIsReadOnlyMode from '../../hooks/useIsReadOnlyMode';
import DynamicGridHeader from '../DynamicGridHeader';
import { cellNumberEditor } from '../GridCellEditors';
import { numericCellBody, textCellBody } from '../GridService';
import { DataTableStyled } from '../StyledGrid';
import useDynamicGridWithEmptyFirstRow from '../useDynamicGridWithEmptyFirstRow';
import useGridRowSelectionViaCheckbox from '../useGridRowSelectionViaCheckbox';
import { useContentDeclarationTerminologyHelper } from '../../hooks/useContentDeclarationTerminologyHelper';

const DangerMaterialsGrid: React.FunctionComponent<{
  epdVersionId: string;
  errors?: any;
  contentDeclarationType: EPDContentDeclarationProductType;
}> = ({ epdVersionId, errors, contentDeclarationType }) => {
  const isReadOnly = useIsReadOnlyMode();
  const { replaceInString, placeholders } = useContentDeclarationTerminologyHelper();

  const dangerMaterialsLookup = useDangerMaterialsLookup().data;

  const rows = useEpdProductDangerMaterials(epdVersionId, contentDeclarationType).data;
  const createMutation = useCreateEpdProductDangerMaterial(epdVersionId, contentDeclarationType);
  const updateMutation = useUpdateEpdProductDangerMaterial(epdVersionId, contentDeclarationType);
  const deleteMutation = useDeleteProductDangerMaterials(epdVersionId, contentDeclarationType);

  const { dataTableProps, checkboxColumnNode, handleDeleteSelected, isDeleteDisabled, gridId } =
    useGridRowSelectionViaCheckbox<ProductDangerMaterialModel>(isReadOnly, deleteMutation);

  const rowClassName = () => ({ 'p-disabled': isReadOnly });

  const { gridData, handleAddRow, handleCellEdit, saveModifiedGridData } =
    useDynamicGridWithEmptyFirstRow<ProductDangerMaterialModel>({
      rows,
      createMutation,
      updateMutation,
      emptyEntity: {
        epdVersionId,
        weightPercent: null,
        declareContentType: contentDeclarationType,
        dangerMaterialLookupId: '',
        name: '',
        ecNo: '',
        casNo: '',
      },
    });

  const header = (
    <DynamicGridHeader
      caption="Hazardous and toxic substances"
      onAddRow={handleAddRow}
      onDeleteRows={handleDeleteSelected}
      deleteRowsDisabled={isDeleteDisabled}
      error={
        errors?.dangerMaterialsTable?.general ||
        errors?.dangerMaterialsTable?.weightPercentTotal ||
        errors?.dangerMaterialsTable
      }
    />
  );

  const selectEditor = (options: ColumnEditorOptions, lookupIdField: string) => {
    return (
      <Dropdown
        filter
        style={{ width: '100%', height: '38px', maxWidth: '27rem' }}
        value={options.rowData[lookupIdField] || options.value}
        optionLabel="name"
        optionValue="id"
        options={dangerMaterialsLookup}
        onChange={(e: DropdownChangeEvent) => {
          const selectedOption = dangerMaterialsLookup?.find((x) => x.id === e.value);
          options.editorCallback?.(selectedOption?.name ?? '');

          const { rowData, field } = options;
          const newData = {
            ...rowData,
            [lookupIdField || field]: e.value,
            [field]: selectedOption?.name ?? '',
          };
          saveModifiedGridData(newData);
        }}
        itemTemplate={(option) => (
          <div
            title={option.name}
            style={{
              maxWidth: '40rem',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontSize: '12px',
            }}
          >
            {option.name}
          </div>
        )}
      />
    );
  };

  return (
    <DataTableStyled
      key={gridId}
      header={header}
      value={gridData}
      dataKey="ord"
      tableStyle={{ minWidth: '49rem', tableLayout: 'fixed' }}
      showGridlines
      editMode="cell"
      rowClassName={rowClassName}
      {...dataTableProps}
    >
      {checkboxColumnNode}

      <Column
        style={{ wordBreak: 'break-all' }}
        field="name"
        header="Hazardous/Toxic substances from the candidate list"
        editor={isReadOnly ? undefined : (options) => selectEditor(options, 'dangerMaterialLookupId')}
        body={textCellBody({ placeholder: 'Select' })}
      />

      <Column field="ecNo" header="EC No." style={{ width: '7rem' }} />
      <Column field="casNo" header="CAS No." style={{ width: '7rem' }} />

      <Column
        field="weightPercent"
        header={replaceInString('Weight per functional or declared unit %')}
        editor={isReadOnly ? undefined : cellNumberEditor}
        onCellEditComplete={handleCellEdit}
        style={{ width: '15rem' }}
        body={numericCellBody({ placeholder: placeholders.typeWeight })}
      />
    </DataTableStyled>
  );
};

export default DangerMaterialsGrid;
